import { useQuery } from '@tanstack/react-query';
import { useParams, useSearchParams } from 'react-router';
import { LocaleLink as Link } from '../../i18n/locale-link';
import { localePath } from '../../i18n/locale-path';
import { countryConfig } from '../../country';
import { Seo } from '../../components/seo';
import { TenderList } from '../../components/tender-list';
import { QueryError, TenderListSkeleton } from '../../components/query-error';
import { usePageTitle } from '../../lib/page-title';
import { tenderPath } from '../../lib/slug';
import { Pagination } from '../../components/pagination';
import { listingPageHref, resolveListingPage } from '../../lib/pagination-paths';
import { UpgradeBanner } from '../../components/upgrade-banner';
import { fetchCatalog, fetchRegions } from '../../lib/catalog-client';
import { useLocale, useT } from '../../i18n/locale-context';
import { ruRegionLocative } from '../../i18n/region-locative';
import { NotFound } from '../not-found';

const PAGE_SIZE = 20;

export function Region() {
  const { slug, page: pageParam } = useParams<{ slug: string; page?: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const page = resolveListingPage(pageParam, searchParams.get('page'));
  const offset = (page - 1) * PAGE_SIZE;
  /** Root of this hub's crawlable pager (`/region/<slug>/page/N`). */
  const basePath = slug ? `/region/${slug}` : null;
  const { locale } = useLocale();
  const t = useT();

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['catalog', 'region', slug, page, locale],
    queryFn: () => fetchCatalog({ region: slug, offset, limit: PAGE_SIZE, lang: locale }),
    enabled: !!slug,
  });

  /*
   * An unknown slug used to render a perfectly plausible region page.
   *
   * `regionName` falls back to the raw slug, so `/region/fergana` produced
   * «Тендеры в fergana» as the H1 and the <title>, with `index, follow` in the
   * client-rendered robots meta — while nginx had already sent
   * `x-robots-tag: noindex` on the SPA fallback. Crawlers obey the header, so
   * nothing is indexed today; the page just asserts the opposite of what the
   * response said, on content that is nothing.
   *
   * Safe to 404 because real region pages are PRERENDERED — their count and
   * listing are in the served HTML, so a valid region never passes through an
   * empty or loading state that could be mistaken for "unknown" (verified over
   * repeated hard reloads on /region/FER).
   *
   * ⚠️ Gated on the region list having actually LOADED, and matched
   * case-insensitively. `regions === undefined` means "not known yet", not "not
   * a region" — 404-ing during that window would break every valid page. The
   * case-insensitive compare keeps `/region/fer` behaving exactly as it does
   * today rather than turning a working lowercase URL into a 404 as a side
   * effect of this fix.
   */
  const { data: regions } = useQuery({
    queryKey: ['catalog', 'regions', locale],
    queryFn: () => fetchRegions(locale),
    staleTime: 5 * 60_000,
  });
  const slugIsUnknown =
    !!slug && !!regions && !regions.some((r) => r.slug.toLowerCase() === slug.toLowerCase());

  const totalPages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;

  function goToPage(nextPage: number) {
    const next = new URLSearchParams(searchParams);
    if (nextPage <= 1) next.delete('page');
    else next.set('page', String(nextPage));
    setSearchParams(next);
  }

  // Prefer the human-readable region name from the first result; fall back to slug.
  const regionName = data?.items[0]?.region || slug || 'Region';
  // The RU strings interpolate after the preposition «в», which needs the
  // prepositional case — the API returns the nominative. Uzbek uses a colon
  // construction and takes the name as-is.
  const regionLabel = locale === 'ru' ? ruRegionLocative(slug, regionName) : regionName;
  usePageTitle(t('region.title', { region: regionLabel }));

  // Placed AFTER every hook — bailing earlier would change the hook order
  // between renders, which React forbids. Renders the shared 404 (noIndex, no
  // canonical) instead of a region page named after whatever was in the URL.
  if (slugIsUnknown) return <NotFound />;

  return (
    <>
      <Seo
        // Page 2+ MUST get its own title/description — see the same note in category.tsx.
        // /region/TSH/page/7 and /region/TSH/page/34 were byte-identical before 2026-08-12.
        title={
          page > 1
            ? t('region.seoTitlePaged', { region: regionLabel, page })
            : t('region.seoTitle', { region: regionLabel })
        }
        description={
          page > 1
            ? t('region.seoDescPaged', {
                region: regionLabel,
                page,
                totalPages,
                count: data?.total ?? 0,
              })
            : t('region.seoDesc', { region: regionLabel, count: data?.total ?? 0 })
        }
        // SELF-canonical per page. Canonicalizing /page/N back to page 1 would
        // ask Google to drop the paginated hubs — and they are the only thing
        // giving this hub's tenders past the first 20 an internal link.
        canonical={basePath ? listingPageHref(basePath, page) : undefined}
        prev={basePath && page > 1 ? listingPageHref(basePath, page - 1) : undefined}
        next={basePath && page < totalPages ? listingPageHref(basePath, page + 1) : undefined}
        lang={locale}
        jsonLd={{
          '@context': 'https://schema.org',
          '@graph': [
            {
              // Was an empty shell (name only). Now lists the tenders rendered
              // on this hub, in display order, at their canonical URLs — the
              // same pattern the home catalog uses.
              '@type': 'ItemList',
              name: t('region.title', { region: regionLabel }),
              itemListElement: (data?.items ?? []).map((item, i) => ({
                '@type': 'ListItem',
                position: i + 1,
                url: `${countryConfig.brand.siteUrl}${localePath(tenderPath(item.slug, item.id), locale)}`,
                name: item.title,
              })),
            },
            {
              // Home → Region breadcrumb (Google renders it in the SERP).
              '@type': 'BreadcrumbList',
              itemListElement: [
                {
                  '@type': 'ListItem',
                  position: 1,
                  name: t('nav.catalog'),
                  item: `${countryConfig.brand.siteUrl}${localePath('/', locale)}`,
                },
                {
                  '@type': 'ListItem',
                  position: 2,
                  name: regionName,
                  item: `${countryConfig.brand.siteUrl}${localePath(`/region/${slug ?? ''}`, locale)}`,
                },
              ],
            },
          ],
        }}
      />

      <Link
        to="/"
        className="mb-4 inline-block text-[13px] font-medium text-ink-soft transition-colors hover:text-brand"
      >
        {t('region.backToCatalog')}
      </Link>

      <h1 className="sr-only">{t('region.title', { region: regionLabel })}</h1>
      {/* Unique, indexable intro so the hub isn't a zero-prose list of cards
          (thin-content flag). Rendered only when data is present so the count is
          real and it lands in the prerendered HTML. Uses the locative regionLabel. */}
      {!isLoading && data && (
        <p className="mb-4 max-w-3xl text-[13px] leading-[1.7] text-ink-soft">
          {t('region.intro', { region: regionLabel, count: data.total })}
        </p>
      )}
      <p className="mb-4 text-[13px] text-ink-muted">
        {isLoading ? t('region.loading') : t('region.found', { count: data?.total ?? 0 })}
      </p>

      {isLoading ? (
        <TenderListSkeleton />
      ) : isError ? (
        <QueryError onRetry={() => void refetch()} />
      ) : (
        <>
          <TenderList items={data?.items ?? []} />
          {data && (
            <Pagination
              page={page}
              limit={PAGE_SIZE}
              total={data.total}
              onPageChange={goToPage}
              hrefFor={basePath ? (n) => listingPageHref(basePath, n) : undefined}
            />
          )}
        </>
      )}

      {/* The banner carries no margin of its own — see its note. */}
      <div className="mt-5">
        <UpgradeBanner />
      </div>
    </>
  );
}
